import React, { useState, useEffect } from 'react';
import { X, CreditCard, AlertCircle } from 'lucide-react';
import { createCheckoutSession } from '../../../utils/stripe/api';
import { useAuthStore } from '../../../stores/authStore';

// Define base URL for redirects
const baseURL = window.location.origin;

interface PaymentModalProps {
  credits: number;
  amount: number;
  onClose: () => void;
}

export function PaymentModal({ credits, amount, onClose }: PaymentModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setError(null);
    setIsProcessing(true);

    try {
      if (!user?.id) {
        throw new Error('ユーザー情報が取得できません');
      }

      // Create checkout session with the price ID
      const priceId = credits === 500 ? 'price_1RIJRnFRwEl5oZnbCuKZyuZe' : 'price_1RIJSJFRwEl5oZnbnTlzOckM';
      const session = await createCheckoutSession(
        priceId,
        'payment', 
        user.id,
        {
          type: 'ai_credits',
          credits: credits.toString(),
          user_email: user.email || '',
        }, 
        1, // Quantity
        `${baseURL}/stripe-success?session_id={CHECKOUT_SESSION_ID}`,
        `${baseURL}/stripe-cancel`
      );

      if (session?.url) {
        // Redirect to Stripe Checkout
        window.location.href = session.url;
      } else {
        throw new Error('チェックアウトセッションの作成に失敗しました');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : '予期しないエラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">AIクレジットを購入</h3>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-900">
                AIクレジット {credits.toLocaleString()}回分
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-gray-900">
                ¥{amount.toLocaleString()}
              </span>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md mb-6">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isProcessing}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <CreditCard className="w-4 h-4 animate-pulse" />
                処理中...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4" />
                ¥{amount.toLocaleString()}で購入する
              </>
            )}
          </button>
          
          <p className="text-xs text-gray-500 mt-4 text-center">
            Stripeの安全な決済ページに移動します
          </p>
        </form>
      </div>
    </div>
  );
}